import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, ShieldCheck, UserCog } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const EMPTY = { email: "", password: "", name: "", role: "operator" };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (u) => { setEditing(u); setForm({ ...EMPTY, ...u, password: "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { email: form.email, name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (u) => {
    if (u.id === me?.id) return toast.error("You cannot delete yourself");
    if (!window.confirm(`Delete user "${u.email}"?`)) return;
    await api.delete(`/users/${u.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-6 md:p-10">
      <PageHeader
        title="Users"
        subtitle="Administrators have full access. Operators can only operate live games."
        testid="page-users"
        action={
          <Button data-testid="create-user-btn" onClick={startCreate} className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm">
            <Plus size={16} className="mr-2" /> New user
          </Button>
        }
      />

      <div className="border border-navy-border rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-navy-surface text-[10px] font-heading uppercase tracking-widest text-ink-muted">
          <div className="col-span-4">Email</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">Created</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-ink-muted">No users.</div>
        ) : items.map((u) => (
          <div key={u.id} className="grid grid-cols-12 items-center px-4 py-3 border-t border-navy-border" data-testid={`user-row-${u.id}`}>
            <div className="col-span-4 font-mono text-white">{u.email}{u.id === me?.id && <span className="text-orange ml-2 text-xs uppercase tracking-widest">(you)</span>}</div>
            <div className="col-span-3 text-ink-secondary">{u.name || "—"}</div>
            <div className="col-span-2">
              <span className={`text-xs font-heading uppercase tracking-widest px-2 py-0.5 rounded-sm inline-flex items-center gap-1 ${u.role === "admin" ? "bg-orange/20 text-orange" : "bg-navy-surface text-ink-secondary border border-navy-border"}`}>
                {u.role === "admin" ? <ShieldCheck size={12} /> : <UserCog size={12} />} {u.role}
              </span>
            </div>
            <div className="col-span-2 text-ink-muted text-xs font-mono">{(u.created_at || "").slice(0, 10)}</div>
            <div className="col-span-1 flex justify-end gap-1">
              <Button data-testid={`edit-user-${u.id}`} size="icon" variant="ghost" onClick={() => startEdit(u)} className="text-ink-secondary hover:text-orange hover:bg-navy-surface"><Pencil size={14} /></Button>
              <Button data-testid={`delete-user-${u.id}`} size="icon" variant="ghost" onClick={() => remove(u)} disabled={u.id === me?.id} className="text-ink-secondary hover:text-court-red hover:bg-navy-surface disabled:opacity-30"><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-navy-deep border-navy-border text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-2xl">{editing ? "Edit user" : "New user"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="user-form">
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Email</Label>
              <Input data-testid="user-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-navy border-navy-border mt-2 rounded-sm" />
            </div>
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Name</Label>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-navy border-navy-border mt-2 rounded-sm" />
            </div>
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="bg-navy border-navy-border text-white mt-2 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-navy-deep border-navy-border text-white">
                  <SelectItem value="admin">Administrator (full access)</SelectItem>
                  <SelectItem value="operator">Operator (games only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">
                Password {editing && <span className="text-ink-muted normal-case tracking-normal"> (leave blank to keep current)</span>}
              </Label>
              <Input
                data-testid="user-password-input"
                type="password"
                minLength={editing ? undefined : 6}
                required={!editing}
                value={form.password || ""}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="bg-navy border-navy-border mt-2 rounded-sm font-mono"
              />
            </div>
            <DialogFooter>
              <Button data-testid="user-submit-btn" type="submit" className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm">
                {editing ? "Save changes" : "Create user"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
